import { Button } from '@app/components/various/Button';
import { RadioCard } from '@app/components/various/Fields/RadioCards';
import { RadioCardsField } from '@app/components/various/Form/FormField/RadioCardsField';
import {
  getOrderDetails,
  getShouldShowMoreShippingAddresses,
  setShouldShowMoreShippingAddresses,
  updateTotalsByShippingAddress,
} from '@app/ducks';
import { getCountryByShippingAddressId, getShippingAddressId } from '@app/logic/Orders';
import { isTemporaryShipping } from '@app/utils/guards';
import { useConfirmationGuard } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { omit } from '@app/utils/omit';
import { Addresses, Form, Radio } from '@typings';
import React from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ShippingAddressRadioBody } from './ShippingAddressRadioBody';

type RadioGroupProps<T extends string> = Omit<Radio.Group<T>, 'value' | 'describedById' | 'onChange' | 'onBlur' | 'options'>;

interface Props<T extends string> extends RadioGroupProps<T>, Form.FieldProps {
  options: (Addresses.Shipping | Addresses.TemporaryShipping)[];
  className?: string;
}

const EMPTY_COUNTRY_PLACEHOLDER = '-';
export const SHIPPING_ADDRESSES_TO_SHOW = 2;

export const ShippingAddressRadioGroupField = <T extends string>(props: Props<T>) => {
  const { helpMessage, label, isRequired = false, name, options } = props;

  const { field } = useController({ name });
  const { t } = useTranslation(['common', 'confirmationConfig']);
  const orderDetails = useSelector(getOrderDetails);
  const confirmationGuard = useConfirmationGuard();
  const dispatch = useDispatch();
  const shouldShowMoreShippingAddresses = useSelector(getShouldShowMoreShippingAddresses);
  const handleToggleShowMore = () => {
    dispatch(setShouldShowMoreShippingAddresses(!shouldShowMoreShippingAddresses));
  };
  const shouldDisplayShowMore = options.length > SHIPPING_ADDRESSES_TO_SHOW;

  const changeShippingAddress = (event: string) => {
    field.onChange(event);
    field.onBlur();
    const shippingAddress = getShippingAddressId(orderDetails.account?.shippingAddresses, event);

    if (!isDefined(shippingAddress)) {
      return;
    }

    dispatch(
      updateTotalsByShippingAddress(
        isTemporaryShipping(shippingAddress) ?
          { shippingAddress: omit(shippingAddress, ['isTemporary', 'shippingAddress']) }
        : { shippingAddressId: event },
      ),
    );
  };

  const handleChange = (value: string) => {
    const candidateCountryByShippingAddressId = getCountryByShippingAddressId(orderDetails.account?.shippingAddresses, value);
    const candidateCountryCode =
      isDefined(candidateCountryByShippingAddressId) && !isEmpty(candidateCountryByShippingAddressId) ?
        candidateCountryByShippingAddressId
      : EMPTY_COUNTRY_PLACEHOLDER;
    const currentCountryByShippingAddressId = getCountryByShippingAddressId(orderDetails.account?.shippingAddresses, field.value);
    const currentCountryCode =
      isDefined(currentCountryByShippingAddressId) && !isEmpty(currentCountryByShippingAddressId) ?
        currentCountryByShippingAddressId
      : EMPTY_COUNTRY_PLACEHOLDER;

    if (candidateCountryCode === currentCountryCode) {
      changeShippingAddress(value);
      field.onChange(value);

      return;
    }

    confirmationGuard(() => ({
      onOk: () => changeShippingAddress(value),
      ...t('confirmationConfig:change_shipping_country_modal', {
        candidateCountryCode,
        currentCountryCode,
        returnObjects: true,
      }),
      okText: t('common:change'),
    }))();
  };

  const addressesAlwaysVisible = React.useMemo(() => options.slice(0, SHIPPING_ADDRESSES_TO_SHOW), [options]);
  const visibleOptions = React.useMemo(
    () => (shouldShowMoreShippingAddresses ? options : addressesAlwaysVisible),
    [addressesAlwaysVisible, options, shouldShowMoreShippingAddresses],
  );

  return (
    <>
      <RadioCardsField
        name={name}
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        onChange={handleChange}
        isRequired={isRequired}
        label={label}
        helpMessage={helpMessage}
      >
        {visibleOptions.map(shippingAddressOption => (
          <RadioCard key={shippingAddressOption.shippingAddress} value={shippingAddressOption.shippingAddress} hasDescription>
            {({ isSelected, inputId, describedById }) => (
              <ShippingAddressRadioBody
                inputId={inputId}
                describedbyId={describedById}
                shippingAddress={shippingAddressOption}
                isSelected={isSelected}
              />
            )}
          </RadioCard>
        ))}
      </RadioCardsField>
      {shouldDisplayShowMore && (
        <div className="flex justify-end pt-3">
          <Button variant="text" color="secondary" onClick={handleToggleShowMore}>
            {shouldShowMoreShippingAddresses ? t('common:show_less') : t('common:show_more')}
          </Button>
        </div>
      )}
    </>
  );
};
